import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Typography } from '@mui/material';

const UncountedModules = () => {
  // State for filters and table data
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // Options for status dropdown
  const statusOptions = ['', 'Theft', 'In Store', 'Others'];

  // Fetch unaccounted modules on mount
  useEffect(() => {
    const fetchUnaccountedModules = async () => {
      try {
        const projectId = localStorage.getItem('selectedProject');
        if (!projectId) {
          setError('Project ID not found in local storage');
          return;
        }

        setLoading(true);
        setError(null);

        const response = await axios.post(`${baseURL}find_unaccounted_modules`, {
          projectId,
        });
        console.log('Unaccounted Modules API Response:', response.data);

        if (response.data.status === 'success' && Array.isArray(response.data.data.unaccounted_modules)) {
          let srNoCounter = 1; // Initialize counter for Sr. No
          const modules = response.data.data.unaccounted_modules.flatMap((entry) =>
            entry.unaccounted_modules.flatMap((item, index) =>
              item.modules.map((module, moduleIndex) => ({
                id: `${entry.contractor}_${item.pallet}_${module}_${index}_${moduleIndex}`,
                srNo: srNoCounter++, // Assign sequential Sr. No
                pallet: item.pallet,
                serialNumber: module,
                contractor: entry.contractor,
                status: '',
                remarks: '',
              }))
            )
          );
          setTableData(modules);
          if (modules.length === 0) {
            setError('No unaccounted modules found for this project');
          }
        } else {
          setError(response.data.message || 'Failed to fetch unaccounted modules');
        }
      } catch (err) {
        console.error('Error fetching unaccounted modules:', err);
        setError('Failed to fetch unaccounted modules');
      } finally {
        setLoading(false);
      }
    };
    fetchUnaccountedModules();
  }, []);

  // Handle status change
  const handleStatusChange = (id, value) => {
    setTableData((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, status: value } : row
      )
    );
  };

  // Handle remarks change
  const handleRemarksChange = (id, value) => {
    setTableData((prevData) =>
      prevData.map((row) =>
        row.id === id ? { ...row, remarks: value } : row
      )
    );
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const projectId = localStorage.getItem('selectedProject');
      if (!projectId) {
        setError('Project ID not found in local storage');
        return;
      }

      const reconciliations = tableData
        .filter((row) => row.status) // Only include rows with status
        .map((row) => ({
          pallet: row.pallet,
          module: row.serialNumber,
          contractor: row.contractor,
          reconcile: row.status.toLowerCase().replace(' ', '_'), // Convert to API format (e.g., "In Store" -> "in_store")
          remarks: row.remarks || '',
        }));

      if (reconciliations.length === 0) {
        setError('No modules with status selected to reconcile');
        return;
      }

      setLoading(true);
      setError(null);

      const payload = { projectId, reconciliations };
      console.log('Sending reconciliations payload:', payload);

      const response = await axios.post(`${baseURL}reconcile_unaccounted_modules`, payload);
      console.log('Reconcile API Response:', response.data);

      if (response.data.status === 'success') {
        setTableData([]); // Clear table on success
        setError(null);
        alert('Data submitted successfully!');
      } else {
        setError(response.data.message || 'Failed to reconcile modules');
      }
    } catch (err) {
      console.error('Error reconciling modules:', err);
      setError('Failed to reconcile modules');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <h1 className="flex justify-center text-3xl font-bold text-gray-800 mb-6">Unaccounted Modules</h1>

      {/* Loading and Error */}
      {loading && (
        <Typography align="center" color="textSecondary" style={{ marginBottom: '20px' }}>
          Loading...
        </Typography>
      )}
      {error && (
        <Typography color="error" align="center" style={{ marginBottom: '20px' }}>
          {error}
        </Typography>
      )}

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6 flex flex-wrap gap-4 items-center">
        <div className="flex flex-col">
          <label className="text-sm font-medium text-gray-700 mb-1">Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-500"
            disabled={loading}
          />
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-md mb-6 overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-800">
            <tr>
              <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">Sr. No</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">Pallet</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">Contractor</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">Serial Number</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-center text-xs font-medium text-white uppercase tracking-wider">Remarks</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableData.length > 0 ? (
              tableData.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-center text-sm text-gray-900">{row.srNo}</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-900">{row.pallet}</td>
                  <td className="px-6 py-3 text-center text-xs font-medium">{row.contractor}</td>
                  <td className="px-6 py-4 text-center text-sm text-gray-900">{row.serialNumber}</td>
                  <td className="px-6 py-4 text-center text-sm">
                    <select
                      value={row.status}
                      onChange={(e) => handleStatusChange(row.id, e.target.value)}
                      className="border border-gray-300 rounded-md p-1 text-sm focus:outline-none focus:ring-2 focus:ring-gray-500 w-full"
                      disabled={loading}
                    >
                      {statusOptions.map((option) => (
                        <option key={option} value={option}>
                          {option || 'Select Status'}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-6 py-4 text-center text-sm">
                    <textarea
                      value={row.remarks}
                      onChange={(e) => handleRemarksChange(row.id, e.target.value)}
                      className="border border-gray-300 rounded-md p-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-500 w-full resize-y"
                      rows="2"
                      placeholder="Enter remarks"
                      disabled={loading}
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-sm text-gray-500">
                  No records found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSubmit}
          className="bg-gray-600 text-white px-6 py-2 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:bg-gray-400 disabled:cursor-not-allowed"
          disabled={loading}
        >
          Submit
        </button>
      </div>
    </div>
  );
};

export default UncountedModules;